import React, { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { api, API } from "@/lib/apiClient";
import { Send, Cpu } from "lucide-react";
import { toast } from "sonner";

const SUGGESTIONS = [
  "Which formation beats a high press?",
  "How should I manage a fatigued midfield?",
  "Explain your Hippocampus to Neocortex bridge.",
  "Best player mix for a Champions League away leg?",
];

export default function OctonConsole() {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [engines, setEngines] = useState([]);
  const [activeEngine, setActiveEngine] = useState("");
  const endRef = useRef(null);

  useEffect(() => {
    api.get("/octon/history").then((r) => setMessages(r.data.map((m) => ({ role: m.role, text: m.text })))).catch(() => {});
    api.get("/settings/model").then((r) => { setEngines(r.data.engines || []); setActiveEngine(r.data.active || ""); }).catch(() => {});
  }, []);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  const switchEngine = async (id) => {
    const prev = activeEngine;
    setActiveEngine(id);
    try {
      const r = await api.post("/settings/model", { engine_id: id });
      toast.success(`OCTON brain → ${r.data.label}`);
    } catch (e) {
      setActiveEngine(prev);
      toast.error("Couldn't switch engine");
    }
  };

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || streaming) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", text: msg }, { role: "assistant", text: "" }]);
    setStreaming(true);
    let acc = "";
    try {
      const res = await fetch(`${API}/octon/chat`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ message: msg }),
      });
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buf = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        const parts = buf.split("\n\n");
        buf = parts.pop();
        for (const p of parts) {
          const line = p.replace(/^data:\s*/, "");
          if (!line) continue;
          try {
            const data = JSON.parse(line);
            if (data.delta) {
              acc += data.delta;
              setMessages((m) => { const c = [...m]; c[c.length - 1] = { role: "assistant", text: acc }; return c; });
            }
          } catch (e) {}
        }
      }
    } catch (e) {
      setMessages((m) => { const c = [...m]; c[c.length - 1] = { role: "assistant", text: "OCTON link interrupted." }; return c; });
    } finally {
      setStreaming(false);
    }
  };

  return (
    <div className="flex h-screen flex-col px-6 pt-20 pb-4 sm:px-10 lg:px-16">
      <div className="mb-3 flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="font-tac text-[11px] tracking-[0.35em] txt-cyan">DIRECT LINK · AVA MOTHERSHIP</p>
          <h1 className="font-head text-4xl font-black uppercase tracking-tighter">OCTON Console</h1>
        </div>
        {engines.length > 0 && (
          <label className="flex items-center gap-2 rounded-xl border border-white/10 bg-[#0a0a0a] px-3 py-2" data-testid="engine-picker" data-octon-section="engine picker model claude gemini" data-octon-label="Engine Picker">
            <Cpu size={14} className="txt-cyan" />
            <span className="font-tac text-[9px] tracking-widest text-white/40">BRAIN ENGINE</span>
            <select
              data-testid="engine-select"
              value={activeEngine}
              onChange={(e) => switchEngine(e.target.value)}
              className="bg-transparent text-xs font-semibold text-white outline-none">
              <optgroup label="Claude (Anthropic)">
                {engines.filter((e) => e.family === "Claude").map((e) => (
                  <option key={e.id} value={e.id} className="bg-[#0a0a0a]">{e.label}</option>
                ))}
              </optgroup>
              <optgroup label="Gemini (Google)">
                {engines.filter((e) => e.family === "Gemini").map((e) => (
                  <option key={e.id} value={e.id} className="bg-[#0a0a0a]">{e.label}</option>
                ))}
              </optgroup>
            </select>
          </label>
        )}
      </div>

      <div className="card-tac flex flex-1 flex-col overflow-hidden">
        <div className="flex-1 space-y-4 overflow-y-auto p-5" data-testid="chat-scroll" data-octon-section="console chat conversation octon messages" data-octon-label="OCTON Console Chat">
          {messages.length === 0 && (
            <div className="flex h-full flex-col items-center justify-center text-center">
              <p className="text-white/50">Tap the AVA orb (bottom-right) to talk live — or type to OCTON below.</p>
              <div className="mt-5 grid max-w-lg grid-cols-1 gap-2 sm:grid-cols-2">
                {SUGGESTIONS.map((s) => (
                  <button key={s} data-testid="suggestion-btn" onClick={() => send(s)}
                    className="rounded-xl border border-white/8 px-4 py-3 text-left text-xs text-white/60 transition hover:border-cyan-400/30 hover:text-white">
                    {s}
                  </button>
                ))}
              </div>
            </div>
          )}
          {messages.map((m, i) => (
            <motion.div key={i} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
              className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
              <div className={`max-w-[80%] rounded-2xl px-4 py-3 text-sm leading-relaxed ${
                m.role === "user" ? "bg-cyan-400/15 text-white" : "glass text-white/85"
              }`} data-testid={`msg-${m.role}`}>
                {m.role === "assistant" && <div className="mb-1 font-tac text-[9px] tracking-widest txt-cyan">OCTON</div>}
                {m.text || (m.role === "assistant" && <span className="inline-block h-3 w-3 animate-pulse rounded-full bg-cyan-400" />)}
              </div>
            </motion.div>
          ))}
          <div ref={endRef} />
        </div>

        <div className="border-t border-white/5 p-4">
          <div className="flex items-center gap-2">
            <input
              data-testid="chat-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && send()}
              placeholder="Ask OCTON about tactics, fitness, player mix…"
              className="flex-1 rounded-xl border border-white/10 bg-[#0a0a0a] px-4 py-3 text-sm text-white outline-none focus:border-cyan-400/50"
            />
            <button data-testid="chat-send-btn" onClick={() => send()} disabled={streaming}
              className="flex h-11 w-11 items-center justify-center rounded-xl bg-cyan-400 text-black transition hover:shadow-[0_0_24px_rgba(0,229,255,0.5)] disabled:opacity-40">
              <Send size={18} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
